<script setup lang="ts">
import ElLogo from '../logo-themable/Logo.component.vue'
import OfflineIndicator from '../offline-indicator/Offline.component.vue'
import ThemeSelector from '../theme-selector/ThemeSelector.component.vue'
import LocaleSelector from '../locale-selector/LocaleSelector.component.vue'
import WhatIsNewDialogComponent from '@/components/what-is-new-dialog/WhatIsNewDialog.component.vue'
import { LinkItem, NuxtLinkItem } from '../shared'
import { config } from '@/config'
import { useLocalization } from '@/pages-hooks'

interface TProps {
  lcid: string
  alwaysVisible?: boolean
  currentScrollY: number
}

const props = withDefaults(defineProps<TProps>(), {
  alwaysVisible: false,
})

const thresholdY = 10
const { translate, getLocalizedPathPrefix } = useLocalization()
const analytics = useAnalytics()

const computedVisible = computed(() => {
  return useRoute().path !== '/' || props.alwaysVisible
})

const cssClass = computed(() => {
  const visibleClasses = `bg-topbar content-topbar backdrop-opacity-90 shadow-[0px_5px_5px_-5px_rgba(0,0,0,0.5)]`
  let otherClasses = ``

  if (computedVisible.value) {
    otherClasses = visibleClasses
  } else {
    otherClasses = props.currentScrollY > thresholdY ? visibleClasses : 'bg-none'
  }

  return `topbar 
      z-50 sticky top-0 w-full h-16 
      transition-all duration-300 
      ${otherClasses} 
    `
    .replace(/\n+/, ' ')
    .trim()
})

const mobileNavCss = computed(() => {
  let results = ['sticky top-16 right-0 w-full bg-card content-card transition-all duration-300 lg:hidden']
  if (mopen.value) {
    results.push('flex flex-col')
  } else {
    results.push('hidden')
  }
  return results.join(' ').trim()
})

const logoWrapperCss = computed(() => {
  let opacity = ''

  if (computedVisible.value !== true) {
    opacity = `opacity-${props.currentScrollY > thresholdY ? 100 : 0}`
  }

  return `logo-wrapper transition-all duration-250 text-xs leading-5 ${opacity}`.trim()
})

const onThemeSelected = (themeId: string) => {
  analytics.track('click', `top-bar_theme-selector_${themeId}`)
}

const mopen = ref(false)
const toggleMobileMenu = () => {
  mopen.value = !mopen.value
}

// const onTopbarMenuItemClicked = (key: string) => {
//   analytics.track('click', `top-bar_${key}`)
//   mopen.value = false
// }

const onMobileMenuItemClicked = (key: string) => {
  analytics.track('click', `mobile-menu_${key}`)
  mopen.value = false
}

//const showWhatIsNewDialog = ref(false)
// const onOpenShowWhatIsNewClicked = () => {
//   showWhatIsNewDialog.value = true
// }
// const onWhatsIsNewDialogClose = () => {
//   localStorage.setItem(`whats-new-seen`, config.brand.productVersion)
//   showWhatIsNewDialog.value = false
// }

// // determine if we show the Whats Is New dialog
// const whatsNewSeen = localStorage.getItem('whats-new-seen')
// if (whatsNewSeen) {
//   releasesState.showWhatIsNewDialog = whatsNewSeen ? whatsNewSeen !== config.brand.productVersion : false
// } else {
//   releasesState.showWhatIsNewDialog = true
// }
</script>

<template>
  <header role="banner" :class="cssClass" style="backdrop-filter: blur(8px)">
    <div class="flex h-full items-center justify-between mx-auto w-full max-w-7xl px-6 py-2 lg:px-8">
      <a :href="`/${lcid}`" :class="logoWrapperCss">
        <span class="sr-only">{{ translate('navigation.home') }}</span>
        <ElLogo cssClass="max-w-48 shrink-0" :brandConfig="config.brand" />
      </a>
      <div class="items-center flex gap-x-4">
        <div class="flex items-center gap-x-1.5">
          <OfflineIndicator :label="translate('offline')" :showLabel="false" />
          <LocaleSelector :currentLcid="lcid" />
          <ThemeSelector @themeSelected="onThemeSelected" />
          <div class="relative h-full inline-flex items-center -ml-0.5 lg:hidden">
            <button
              @click="toggleMobileMenu"
              class="size-6 flex items-center content-body m-0 transition duration-150 ease-in-out"
              aria-expanded="false"
            >
              <span class="sr-only">Open menu</span>
              <svg
                class=""
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <g v-if="mopen">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </g>
                <g v-if="!mopen">
                  <line x1="4" x2="20" y1="12" y2="12" />
                  <line x1="4" x2="20" y1="6" y2="6" />
                  <line x1="4" x2="20" y1="18" y2="18" />
                  <!-- <rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 8h10"/><path d="M7 12h10"/><path d="M7 16h10"/> -->
                </g>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>

  <nav role="navigation" :class="mobileNavCss" style="z-index: 49">
    <div class="pt-2 pb-3 px-2 space-y-1">
      <LinkItem :to="`/${lcid}`" :isMobile="true" @clicked="onMobileMenuItemClicked('home')">
        {{ translate('navigation.home') }}
      </LinkItem>

      <NuxtLinkItem
        :to="`${getLocalizedPathPrefix(lcid)}/releases#top`"
        :isMobile="true"
        @clicked="onMobileMenuItemClicked('source')"
        >Source</NuxtLinkItem
      >
      <!-- <NuxtLinkItem
        :to="`${getLocalizedPathPrefix(lcid)}/pricing#top`"
        :isMobile="true"
        @clicked="onMobileMenuItemClicked('pricing')"
        >{{ translate('navigation.pricing') }}</NuxtLinkItem
      > -->
      <LinkItem
        :to="`${getLocalizedPathPrefix(lcid)}/pricing#top`"
        :isMobile="true"
        @clicked="onMobileMenuItemClicked('buy-license')"
      >
        {{ translate('navigation.buyLicense') }}
      </LinkItem>
      <!-- <AnchorLinkItem
        to="#"
        addCss="termly-display-preferences"
        :isMobile="true"
        @clicked="onMobileMenuItemClicked('consent-preferences')"
      >
        {{ translate('navigation.consentPreferences') }}
      </AnchorLinkItem> -->
    </div>
  </nav>

  <!-- <ClientOnly>
    <WhatIsNewDialogComponent v-if="showWhatIsNewDialog" :lcid="lcid" @close="onWhatsIsNewDialogClose" />
  </ClientOnly> -->
</template>
